import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { API_URL } from '../../lib/api-client';
import { logger } from '../../utils/logger';

interface AdminState {
    overview: {
        stats: any | null;
        recentActivities: any[];
        loading: boolean;
        error: string | null;
        lastFetched: number | null;
    };
    users: {
        list: any[];
        pagination: {
            total: number;
            page: number;
            limit: number;
            totalPages: number;
        };
        loading: boolean;
        error: string | null;
        lastFetched: number | null;
    };
    plans: {
        list: any[];
        loading: boolean;
        error: string | null;
        lastFetched: number | null;
    };
    premiumPlans: {
        list: any[];
        loading: boolean;
        error: string | null;
        lastFetched: number | null;
    };
    coupons: {
        list: any[];
        pagination: {
            total: number;
            page: number;
            limit: number;
            totalPages: number;
        };
        loading: boolean;
        error: string | null;
        lastFetched: number | null;
    };
    profileVerification: {
        list: any[];
        pagination: {
            total: number;
            page: number;
            limit: number;
            totalPages: number;
        };
        loading: boolean;
        error: string | null;
        lastFetched: number | null;
    };
    profileEditApprovals: {
        list: any[];
        stats: {
            pending: number;
            approved: number;
            rejected: number;
        };
        currentFilter: 'pending' | 'approved' | 'rejected' | 'all';
        tabCache: Record<string, any[]>;
        loading: boolean;
        error: string | null;
        lastFetched: number | null;
    };
}

const initialState: AdminState = {
    overview: {
        stats: null,
        recentActivities: [],
        loading: false,
        error: null,
        lastFetched: null,
    },
    users: {
        list: [],
        pagination: {
            total: 0,
            page: 1,
            limit: 20,
            totalPages: 0,
        },
        loading: false,
        error: null,
        lastFetched: null,
    },
    plans: {
        list: [],
        loading: false,
        error: null,
        lastFetched: null,
    },
    premiumPlans: {
        list: [],
        loading: false,
        error: null,
        lastFetched: null,
    },
    coupons: {
        list: [],
        pagination: {
            total: 0,
            page: 1,
            limit: 10,
            totalPages: 1,
        },
        loading: false,
        error: null,
        lastFetched: null,
    },
    profileVerification: {
        list: [],
        pagination: {
            total: 0,
            page: 1,
            limit: 20,
            totalPages: 0,
        },
        loading: false,
        error: null,
        lastFetched: null,
    },
    profileEditApprovals: {
        list: [],
        stats: {
            pending: 0,
            approved: 0,
            rejected: 0,
        },
        currentFilter: 'pending',
        tabCache: {},
        loading: false,
        error: null,
        lastFetched: null,
    },
};

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Async Thunks
export const fetchAdminOverview = createAsyncThunk(
    'admin/fetchOverview',
    async (forceRefresh: boolean = false, { getState, rejectWithValue }) => {
        try {
            const state = getState() as { admin: AdminState };
            if (!forceRefresh && state.admin.overview.lastFetched && Date.now() - state.admin.overview.lastFetched < CACHE_DURATION) {
                return null;
            }

            const token = localStorage.getItem('token');
            const [usersRes, paymentsRes, activitiesRes] = await Promise.all([
                axios.get(`${API_URL}/admin/users`, { headers: { Authorization: `Bearer ${token}` } }),
                axios.get(`${API_URL}/admin/payments`, { headers: { Authorization: `Bearer ${token}` } }),
                axios.get(`${API_URL}/activities?limit=5`, { headers: { Authorization: `Bearer ${token}` } })
            ]);

            const users = usersRes.data.data?.users || usersRes.data.data || [];
            const payments = paymentsRes.data.data || [];
            const activities = activitiesRes.data.data?.activities || [];

            // Logic from AdminDashboardReal.tsx
            const totalRevenue = payments
                .filter((p: any) => p.status === 'success' || p.status === 'completed')
                .reduce((sum: number, p: any) => sum + (p.amount || 0), 0);

            const activeUsers = users.filter((u: any) => u.isActive).length;

            return {
                stats: {
                    totalUsers: users.length,
                    activeUsers,
                    totalRevenue,
                    successRate: users.length > 0 ? Math.round((activeUsers / users.length) * 100) : 0,
                    activeSubscriptions: users.filter((u: any) => u.plan && u.plan !== 'Free').length,
                },
                recentActivities: activities
            };
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch overview');
        }
    }
);

export const fetchAdminUsers = createAsyncThunk(
    'admin/fetchUsers',
    async (params: { page?: number; limit?: number; search?: string; status?: string; plan?: string; role?: string } = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/admin/users`, {
                params,
                headers: { Authorization: `Bearer ${token}` }
            });
            const responseData = response.data.data || response.data;
            return {
                users: responseData.users || [],
                pagination: responseData.pagination || { total: (responseData.users || []).length, page: params.page || 1, limit: params.limit || 20, totalPages: 1 }
            };
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch users');
        }
    }
);

export const fetchAdminPlans = createAsyncThunk(
    'admin/fetchPlans',
    async (_, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/admin/plans`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const responseData = response.data.data;
            return Array.isArray(responseData) ? responseData : responseData.plans || [];
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch plans');
        }
    }
);

export const fetchAdminPremiumPlans = createAsyncThunk(
    'admin/fetchPremiumPlans',
    async (_, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/admin/premium-plans`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const responseData = response.data.data;
            return Array.isArray(responseData) ? responseData : responseData?.premiumPlans || [];
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch premium plans');
        }
    }
);

export const fetchAdminCoupons = createAsyncThunk(
    'admin/fetchCoupons',
    async (params: { page?: number; limit?: number } = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/coupons`, {
                params,
                headers: { Authorization: `Bearer ${token}` }
            });
            const responseData = response.data.data;
            return {
                coupons: responseData.coupons || [],
                pagination: {
                    total: responseData.total || (responseData.coupons || []).length,
                    page: params.page || 1,
                    limit: params.limit || 10,
                    totalPages: responseData.totalPages || 1
                }
            };
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch coupons');
        }
    }
);

// Profile Verification Thunks
export const fetchPendingProfiles = createAsyncThunk(
    'admin/fetchPendingProfiles',
    async (params: { page?: number; limit?: number; forceRefresh?: boolean } = {}, { getState, rejectWithValue }) => {
    try {
        const state = getState() as { admin: AdminState };
        const { lastFetched } = state.admin.profileVerification;

        // Use cache if available and not forcing refresh
        if (!params.forceRefresh && lastFetched && Date.now() - lastFetched < CACHE_DURATION) {
            logger.info('[Admin] Using cached pending profiles data');
            return null;
        }

        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/admin/pending-profiles`, {
            params: {
                page: params.page || 1,
                limit: params.limit || 20
            },
            headers: { Authorization: `Bearer ${token}` }
        });

        const responseData = response.data.data;
        return {
            profiles: responseData.profiles || [],
            pagination: {
                total: responseData.pagination?.total || 0,
                page: responseData.pagination?.page || params.page || 1,
                limit: responseData.pagination?.limit || params.limit || 20,
                totalPages: responseData.pagination?.pages || 0
            }
        };
    } catch (error: any) {
        return rejectWithValue(error.response?.data?.message || 'Failed to fetch pending profiles');
    }
}
);

export const approveProfile = createAsyncThunk(
    'admin/approveProfile',
    async({ userId, adminNotes }: { userId: string; adminNotes?: string }, { rejectWithValue }) => {
    try {
        const token = localStorage.getItem('token');
        const response = await axios.post(
            `${API_URL}/admin/profiles/${userId}/approve`,
            { adminNotes },
            { headers: { Authorization: `Bearer ${token}` } }
        );
        return { userId, data: response.data };
    } catch (error: any) {
        return rejectWithValue(error.response?.data?.message || 'Failed to approve profile');
    }
}
);

export const rejectProfile = createAsyncThunk(
    'admin/rejectProfile',
    async({ userId, reason, adminNotes }: { userId: string; reason: string; adminNotes?: string }, { rejectWithValue }) => {
    try {
        const token = localStorage.getItem('token');
        const response = await axios.post(
            `${API_URL}/admin/profiles/${userId}/reject`,
            { reason, adminNotes },
            { headers: { Authorization: `Bearer ${token}` } }
        );
        return { userId, data: response.data };
    } catch (error: any) {
        return rejectWithValue(error.response?.data?.message || 'Failed to reject profile');
    }
}
);

// Profile Edit Approvals Thunks
export const fetchProfileChanges = createAsyncThunk(
    'admin/fetchProfileChanges',
    async (params: { status?: 'pending' | 'approved' | 'rejected' | 'all'; forceRefresh?: boolean } = {}, { getState, rejectWithValue }) => {
    try {
        const state = getState() as { admin: AdminState };
        const { tabCache, currentFilter, lastFetched } = state.admin.profileEditApprovals;

        // Use cache if available and not forcing refresh
        const filterKey = params.status || currentFilter;
        if (!params.forceRefresh && tabCache[filterKey] && lastFetched && Date.now() - lastFetched < CACHE_DURATION) {
            logger.info(`[Admin] Using cached profile changes data for filter: ${filterKey}`);
            return { changes: tabCache[filterKey], stats: state.admin.profileEditApprovals.stats, fromCache: true };
        }

        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/admin/pending-profile-changes`, {
            params: {
                status: params.status || 'pending',
                limit: 100
            },
            headers: { Authorization: `Bearer ${token}` }
        });

        const responseData = response.data.data;
        return {
            changes: responseData.changes || [],
            stats: responseData.stats || { pending: 0, approved: 0, rejected: 0 },
            filter: params.status || 'pending',
            fromCache: false
        };
    } catch (error: any) {
        return rejectWithValue(error.response?.data?.message || 'Failed to fetch profile changes');
    }
}
);

export const approveProfileChange = createAsyncThunk(
    'admin/approveProfileChange',
    async({ changeId, editedData }: { changeId: string; editedData?: any }, { rejectWithValue }) => {
    try {
        const token = localStorage.getItem('token');
        const response = await axios.post(
            `${API_URL}/admin/pending-profile-changes/${changeId}/approve`,
            editedData ? { editedData } : {},
            { headers: { Authorization: `Bearer ${token}` } }
        );
        return { changeId, data: response.data };
    } catch (error: any) {
        return rejectWithValue(error.response?.data?.message || 'Failed to approve profile change');
    }
}
);

export const rejectProfileChange = createAsyncThunk(
    'admin/rejectProfileChange',
    async({ changeId, reason }: { changeId: string; reason: string }, { rejectWithValue }) => {
    try {
        const token = localStorage.getItem('token');
        const response = await axios.post(
            `${API_URL}/admin/pending-profile-changes/${changeId}/reject`,
            { reason },
            { headers: { Authorization: `Bearer ${token}` } }
        );
        return { changeId, data: response.data };
    } catch (error: any) {
        return rejectWithValue(error.response?.data?.message || 'Failed to reject profile change');
    }
}
);

const adminSlice = createSlice({
    name: 'admin',
    initialState,
    reducers: {
        clearAdminError: (state, action: { payload: keyof AdminState }) => {
            (state[action.payload] as any).error = null;
        }
    },
    extraReducers: (builder) => {
        // Overview
        builder
            .addCase(fetchAdminOverview.pending, (state) => {
                state.overview.loading = true;
                state.overview.error = null;
            })
            .addCase(fetchAdminOverview.fulfilled, (state, action) => {
                state.overview.loading = false;
                if (action.payload) {
                    state.overview.stats = action.payload.stats;
                    state.overview.recentActivities = action.payload.recentActivities;
                    state.overview.lastFetched = Date.now();
                }
            })
            .addCase(fetchAdminOverview.rejected, (state, action) => {
                state.overview.loading = false;
                state.overview.error = action.payload as string;
            });

        // Users
        builder
            .addCase(fetchAdminUsers.pending, (state) => {
                state.users.loading = true;
                state.users.error = null;
            })
            .addCase(fetchAdminUsers.fulfilled, (state, action) => {
                state.users.loading = false;
                state.users.list = action.payload.users;
                state.users.pagination = action.payload.pagination;
                state.users.lastFetched = Date.now();
            })
            .addCase(fetchAdminUsers.rejected, (state, action) => {
                state.users.loading = false;
                state.users.error = action.payload as string;
            });

        // Plans
        builder
            .addCase(fetchAdminPlans.pending, (state) => {
                state.plans.loading = true;
                state.plans.error = null;
            })
            .addCase(fetchAdminPlans.fulfilled, (state, action) => {
                state.plans.loading = false;
                state.plans.list = action.payload;
                state.plans.lastFetched = Date.now();
            })
            .addCase(fetchAdminPlans.rejected, (state, action) => {
                state.plans.loading = false;
                state.plans.error = action.payload as string;
            });

        // Premium Plans
        builder
            .addCase(fetchAdminPremiumPlans.pending, (state) => {
                state.premiumPlans.loading = true;
                state.premiumPlans.error = null;
            })
            .addCase(fetchAdminPremiumPlans.fulfilled, (state, action) => {
                state.premiumPlans.loading = false;
                state.premiumPlans.list = action.payload;
                state.premiumPlans.lastFetched = Date.now();
            })
            .addCase(fetchAdminPremiumPlans.rejected, (state, action) => {
                state.premiumPlans.loading = false;
                state.premiumPlans.error = action.payload as string;
            });

        // Coupons
        builder
            .addCase(fetchAdminCoupons.pending, (state) => {
                state.coupons.loading = true;
                state.coupons.error = null;
            })
            .addCase(fetchAdminCoupons.fulfilled, (state, action) => {
                state.coupons.loading = false;
                state.coupons.list = action.payload.coupons;
                state.coupons.pagination = action.payload.pagination;
                state.coupons.lastFetched = Date.now();
            })
            .addCase(fetchAdminCoupons.rejected, (state, action) => {
                state.coupons.loading = false;
                state.coupons.error = action.payload as string;
            });

        // Profile Verification
        builder
            .addCase(fetchPendingProfiles.pending, (state) => {
                state.profileVerification.loading = true;
                state.profileVerification.error = null;
            })
            .addCase(fetchPendingProfiles.fulfilled, (state, action) => {
                state.profileVerification.loading = false;
                if (action.payload) {
                    state.profileVerification.list = action.payload.profiles;
                    state.profileVerification.pagination = action.payload.pagination;
                    state.profileVerification.lastFetched = Date.now();
                }
            })
            .addCase(fetchPendingProfiles.rejected, (state, action) => {
                state.profileVerification.loading = false;
                state.profileVerification.error = action.payload as string;
            })
            // Approve Profile - Optimistic Update
            .addCase(approveProfile.fulfilled, (state, action) => {
                // Remove from pending list
                state.profileVerification.list = state.profileVerification.list.filter(
                    (profile) => profile._id !== action.payload.userId
                );
                // Update pagination total
                if (state.profileVerification.pagination.total > 0) {
                    state.profileVerification.pagination.total -= 1;
                }
            })
            // Reject Profile - Optimistic Update
            .addCase(rejectProfile.fulfilled, (state, action) => {
                // Remove from pending list
                state.profileVerification.list = state.profileVerification.list.filter(
                    (profile) => profile._id !== action.payload.userId
                );
                // Update pagination total
                if (state.profileVerification.pagination.total > 0) {
                    state.profileVerification.pagination.total -= 1;
                }
            });

        // Profile Edit Approvals
        builder
            .addCase(fetchProfileChanges.pending, (state) => {
                state.profileEditApprovals.loading = true;
                state.profileEditApprovals.error = null;
            })
            .addCase(fetchProfileChanges.fulfilled, (state, action) => {
                state.profileEditApprovals.loading = false;
                if (action.payload.fromCache) {
                    // Data from cache, already in state
                    state.profileEditApprovals.list = action.payload.changes;
                } else {
                    // Fresh data from API
                    state.profileEditApprovals.list = action.payload.changes;
                    state.profileEditApprovals.stats = action.payload.stats;
                    // Update tab cache
                    state.profileEditApprovals.tabCache[action.payload.filter] = action.payload.changes;
                    state.profileEditApprovals.currentFilter = action.payload.filter;
                    state.profileEditApprovals.lastFetched = Date.now();
                }
            })
            .addCase(fetchProfileChanges.rejected, (state, action) => {
                state.profileEditApprovals.loading = false;
                state.profileEditApprovals.error = action.payload as string;
            })
            // Approve Profile Change - Optimistic Update
            .addCase(approveProfileChange.fulfilled, (state, action) => {
                // Remove from current list
                state.profileEditApprovals.list = state.profileEditApprovals.list.filter(
                    (change) => change._id !== action.payload.changeId
                );
                // Update stats
                if (state.profileEditApprovals.stats.pending > 0) {
                    state.profileEditApprovals.stats.pending -= 1;
                }
                // Clear cache to force refresh on next tab visit
                state.profileEditApprovals.tabCache = {};
            })
            // Reject Profile Change - Optimistic Update
            .addCase(rejectProfileChange.fulfilled, (state, action) => {
                // Remove from current list
                state.profileEditApprovals.list = state.profileEditApprovals.list.filter(
                    (change) => change._id !== action.payload.changeId
                );
                // Update stats
                if (state.profileEditApprovals.stats.pending > 0) {
                    state.profileEditApprovals.stats.pending -= 1;
                }
                // Clear cache to force refresh on next tab visit
                state.profileEditApprovals.tabCache = {};
            });
    }
});

export const { clearAdminError } = adminSlice.actions;
export default adminSlice.reducer;
